import { StandardEvents } from '@shopify/events';
import { Component } from '@theme/component';

/**
 * @typedef {Object} SwitchDiscountBadgeRefs
 * @property {HTMLElement} badgeContent
 * @property {HTMLElement} productBadges
 * @property {HTMLElement} variantBadges
 */

/**
 * Badge row above the product title. Owns two containers:
 * - [ref="productBadges"] — static list from switch_product.badges
 * - [ref="variantBadges"] — discount pill + switch_pack.badges for the
 *   selected variant, rebuilt from window.switchBadgeData on variant change
 *
 * Substitution rule (per Per-Variant Badge SPEC):
 *   If the selected variant has a discount OR any switch_pack.badges →
 *     show the variant container, hide the product container.
 *   Else if the product has switch_product.badges →
 *     show the product container, hide the variant container.
 *   Else →
 *     collapse the wrapper entirely.
 *
 * Hidden↔visible state is now toggled instantly via the
 * `.switch-discount-badge--hidden` class (CSS controls visibility only — no
 * transition). Custom motion was removed from this file as part of the
 * site-wide motion-strip pass.
 *
 * @extends {Component<SwitchDiscountBadgeRefs>}
 */
class SwitchDiscountBadge extends Component {
  connectedCallback() {
    super.connectedCallback();
    const section = this.closest('.shopify-section');
    if (!section) return;
    // 4.x: ThemeEvents.variantUpdate -> StandardEvents.productSelect (@shopify/events).
    // Inert on single-variant Switch (Decision #24); the handler guard no-ops until
    // a variant id is present. If multi-variant is ever added, adapt updateBadge to
    // ProductSelectEvent's event.promise/detail shape.
    section.addEventListener(StandardEvents.productSelect, this.updateBadge);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const section = this.closest('.shopify-section');
    if (section) {
      section.removeEventListener(StandardEvents.productSelect, this.updateBadge);
    }
  }

  updateBadge = (event) => {
    const variantId = event.detail?.resource?.id;
    const productId = event.detail?.data?.productId;
    if (!variantId || !productId) return;

    // Combined listings can mount more than one badge block on a page;
    // only react to updates for our own product.
    if (String(productId) !== String(this.dataset.productId)) return;

    const variantData = window.switchBadgeData?.[productId]?.[variantId];
    if (!variantData) return;

    const { productBadges, variantBadges } = this.refs;
    if (!productBadges || !variantBadges) return;

    const hasDiscount = Boolean(variantData.discount);
    const hasCustomBadges = Array.isArray(variantData.badges) && variantData.badges.length > 0;
    const hasProductBadges = this.dataset.hasProductBadges === 'true';

    let targetMode = 'empty';
    if (hasDiscount || hasCustomBadges) targetMode = 'variant';
    else if (hasProductBadges) targetMode = 'product';

    // Rebuild the variant container from data — cheap, and keeps the
    // markup authoritative relative to window.switchBadgeData.
    this.#populateVariant(variantBadges, variantData);

    const applyContainerVisibility = () => {
      productBadges.hidden = targetMode !== 'product';
      variantBadges.hidden = targetMode !== 'variant';
    };

    const willBeHidden = targetMode === 'empty';
    const isCurrentlyHidden = this.classList.contains('switch-discount-badge--hidden');

    if (willBeHidden === isCurrentlyHidden) {
      applyContainerVisibility();
      return;
    }

    if (willBeHidden) {
      this.classList.add('switch-discount-badge--hidden');
      applyContainerVisibility();
      return;
    }

    applyContainerVisibility();
    this.classList.remove('switch-discount-badge--hidden');
  };

  #populateVariant(container, data) {
    container.replaceChildren();
    if (data.discount) {
      container.append(this.#createPill(data.discount));
    }
    if (Array.isArray(data.badges)) {
      for (const b of data.badges) {
        container.append(this.#createPill(b.label, b.bg, b.color));
      }
    }
  }

  #createPill(label, bg, color) {
    const span = document.createElement('span');
    span.className = 'switch-pill';
    span.style.setProperty('--switch-pill-bg', bg || '#CC4B2C');
    span.style.setProperty('--switch-pill-color', color || '#FFFFFF');
    span.textContent = label;
    return span;
  }
}

if (!customElements.get('switch-discount-badge')) {
  customElements.define('switch-discount-badge', SwitchDiscountBadge);
}
